import type { ShapeType } from '@/lib/shapes/types/shape-type'
import { INSET_MAX, INSET_MIN } from '../constants/sides-limits'

/**
 * Inner-radius factor used by `RegularPolygonElement.inner` (proto: string).
 * Mirrors the ratios baked into `regularStarCommandsWithTips` / `pathCommandsForSides`.
 * `0` for plain convex polygons; non-zero values uniquely identify each star variant
 * so the shape can be reconstructed on reload even when frontend `editorShape`
 * metadata is dropped by the API.
 */
export function innerRatioForShapeType(type: ShapeType): number {
  switch (type) {
    case 'star':
      return 0.5
    case 'starone':
      return 0.75
    case 'startwo':
      return 0.4
    default:
      return 0
  }
}

/** Inverse of `innerRatioForShapeType`: maps a stored `inner` ratio to a star variant. */
export function starShapeTypeFromInner(inner: number): ShapeType | null {
  if (!Number.isFinite(inner) || inner <= 0 || inner > 1) return null
  if (Math.abs(inner - 0.75) < 0.05) return 'starone'
  if (Math.abs(inner - 0.4) < 0.05) return 'startwo'
  if (Math.abs(inner - 0.5) < 0.05) return 'star'

  return null
}

/** Convert inset percent (0-100) to a star inner radius ratio (0-1). */
export function innerRatioFromInsetPercent(percent: number): number {
  const p = Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round(percent)))

  return Math.max(0, Math.min(1, 1 - p / 100))
}

/**
 * Decode `RegularPolygonElement.inner` from API/checkpoint.
 * Supports legacy ratio format (0..1) and new inset-percent format (0..100).
 */
export function insetPercentFromInner(inner: number): number {
  if (!Number.isFinite(inner)) return 0
  if (inner <= 1) {
    // Legacy payloads stored star inner ratio (0..1). New payloads store inset percent in `inner`.
    // For known legacy star ratios, map ratio -> inset; otherwise treat 0..1 as percent directly.
    const legacyStar = starShapeTypeFromInner(inner)

    if (legacyStar) {
      return Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round((1 - inner) * 100)))
    }

    return Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round(inner)))
  }

  return Math.max(INSET_MIN, Math.min(INSET_MAX, Math.round(inner)))
}
